import React, { useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { OrderTally, CouponValidateResponse } from '../../types/order';
import { couponStatus } from '../../utils/coupon';
import { ORDER_BAR_GRADIENT, ORDER_BAR_TEXT, ORDER_BAR_TEXT_MUTED } from './OrderPageHeader';
import api from '../../services/api';

interface OrderTallyComponentProps {
  tally: OrderTally;
  internetPlanId?: string;
  onCouponApplied?: (coupon: CouponValidateResponse | null) => void;
  appliedCoupon?: CouponValidateResponse | null;
  contractType?: string;
}

function TallyRow({ label, value, bold, strikethrough }: { label: string; value: string; bold?: boolean; strikethrough?: boolean }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
      <Typography variant="body2" sx={{ color: ORDER_BAR_TEXT_MUTED }}>{label}</Typography>
      <Typography
        variant="body2"
        fontWeight={bold ? 700 : 600}
        sx={{
          color: strikethrough ? 'rgba(255,255,255,0.4)' : ORDER_BAR_TEXT,
          textDecoration: strikethrough ? 'line-through' : 'none',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function OrderTallyComponent({ tally, internetPlanId, onCouponApplied, appliedCoupon, contractType = 'no_contract' }: OrderTallyComponentProps) {
  const fmt = (price: number | string) => `$${Number(price).toFixed(2)}`;
  const hasOneTime =
    Number(tally.setup_fees) > 0 ||
    Number(tally.equipment_total) > 0 ||
    Number(tally.delivery_fee) > 0;

  const [couponCode, setCouponCode] = useState('');
  const [couponError, setCouponError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [showCouponInput, setShowCouponInput] = useState(false);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    setValidating(true);
    setCouponError(null);
    try {
      const productIds = internetPlanId ? [internetPlanId] : [];
      const coupon = await api.validateCoupon(couponCode.trim(), productIds);
      onCouponApplied?.(coupon);
      setCouponCode('');
      setShowCouponInput(false);
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setCouponError(typeof detail === 'string' ? detail : 'Invalid or expired coupon code.');
    } finally {
      setValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    onCouponApplied?.(null);
    setCouponError(null);
  };

  const status = couponStatus(appliedCoupon, contractType);
  const discountedMonthly =
    appliedCoupon && status?.discountActive
      ? Math.max(0, Number(tally.total_monthly) - Number(appliedCoupon.discount_amount))
      : null;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 80,
        background: ORDER_BAR_GRADIENT,
        borderRadius: 2,
        p: 3,
        boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      }}
    >
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: ORDER_BAR_TEXT, mb: 1.5 }}>
        Order Summary
      </Typography>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="caption" display="block" sx={{ color: ORDER_BAR_TEXT_MUTED }}>
          Total monthly cost
        </Typography>
        {appliedCoupon && discountedMonthly != null ? (
          <>
            <Typography variant="h5" fontWeight={700} sx={{ color: ORDER_BAR_TEXT, lineHeight: 1.2, mt: 0.25 }}>
              {fmt(discountedMonthly!)}
              <Typography component="span" variant="body2" sx={{ color: ORDER_BAR_TEXT_MUTED, ml: 0.5 }}>
                / month
              </Typography>
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.55)' }}>
              for {appliedCoupon.discount_months} months, then {fmt(tally.total_monthly)}/mo
            </Typography>
          </>
        ) : (
          <Typography variant="h5" fontWeight={700} sx={{ color: ORDER_BAR_TEXT, lineHeight: 1.2, mt: 0.25 }}>
            {fmt(tally.total_monthly)}
            <Typography component="span" variant="body2" sx={{ color: ORDER_BAR_TEXT_MUTED, ml: 0.5 }}>
              / month
            </Typography>
          </Typography>
        )}
      </Box>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 1.5 }} />
      <TallyRow label="Plan" value={`${fmt(tally.plan_monthly)}/mo`} />
      {Number(tally.addon_monthly) > 0 && (
        <TallyRow label="Add-ons" value={`${fmt(tally.addon_monthly)}/mo`} />
      )}
      {Number(tally.voice_monthly) > 0 && (
        <TallyRow label="Voice" value={`${fmt(tally.voice_monthly)}/mo`} />
      )}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 1 }} />
      <TallyRow label="Monthly total" value={`${fmt(tally.total_monthly)}/mo`} bold />

      {hasOneTime && (
        <>
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 1.5 }} />
          <TallyRow label="Setup fees" value={fmt(tally.setup_fees)} />
          <TallyRow label="Equipment" value={fmt(tally.equipment_total)} />
          <TallyRow label="Delivery" value={fmt(tally.delivery_fee)} />
          <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 1 }} />
          <TallyRow label="One-time total" value={fmt(tally.total_one_time)} bold />
        </>
      )}

      {!hasOneTime && (
        <Typography variant="body2" sx={{ color: ORDER_BAR_TEXT_MUTED, mt: 0.5 }}>
          No connection fee
        </Typography>
      )}

      {/* Coupon section */}
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 1.5 }} />

      {appliedCoupon ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 18 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: '#4caf50' }}>
              {appliedCoupon.code}
            </Typography>
            {status?.hasDiscount && (
              <Typography variant="caption" display="block" sx={{ color: ORDER_BAR_TEXT_MUTED }}>
                {status.discountActive
                  ? `-${fmt(appliedCoupon.discount_amount!)}/mo for ${appliedCoupon.discount_months} months`
                  : `Choose a ${status.discountNeeds} for ${fmt(appliedCoupon.discount_amount!)}/mo off`}
              </Typography>
            )}
            {status?.hasBonus && (
              <Typography
                variant="caption"
                display="block"
                sx={{ color: status.bonusActive ? '#4caf50' : '#ffb74d', fontWeight: 600 }}
              >
                {status.bonusActive
                  ? `Includes: ${appliedCoupon.bonus_title}`
                  : `Choose a ${status.bonusNeeds} to receive: ${appliedCoupon.bonus_title}`}
              </Typography>
            )}
          </Box>
          <Tooltip title="Remove coupon">
            <IconButton size="small" onClick={handleRemoveCoupon} sx={{ color: ORDER_BAR_TEXT_MUTED }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <>
          {!showCouponInput ? (
            <Box
              onClick={() => setShowCouponInput(true)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                cursor: 'pointer',
                opacity: 0.75,
                '&:hover': { opacity: 1 },
              }}
            >
              <LocalOfferIcon sx={{ color: ORDER_BAR_TEXT_MUTED, fontSize: 16 }} />
              <Typography variant="body2" sx={{ color: ORDER_BAR_TEXT_MUTED }}>
                Have a promo code?
              </Typography>
            </Box>
          ) : (
            <Collapse in={showCouponInput}>
              <TextField
                size="small"
                placeholder="Enter promo code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
                error={!!couponError}
                helperText={couponError}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {validating ? (
                        <CircularProgress size={16} sx={{ color: ORDER_BAR_TEXT_MUTED }} />
                      ) : (
                        <Chip
                          label="Apply"
                          size="small"
                          onClick={handleApplyCoupon}
                          disabled={!couponCode.trim()}
                          sx={{
                            cursor: 'pointer',
                            bgcolor: 'rgba(255,255,255,0.15)',
                            color: ORDER_BAR_TEXT,
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
                          }}
                        />
                      )}
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: ORDER_BAR_TEXT,
                    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.6)' },
                    '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.8)' },
                  },
                  '& .MuiFormHelperText-root': { color: '#ff8a80' },
                }}
              />
            </Collapse>
          )}
        </>
      )}
    </Box>
  );
}

export default OrderTallyComponent;
